<template>
    <virtualized
        class="pt-table"
        ref="tableRef"
        :dataKey="dataKey"
        :dataSources="usedDataSource"
        :dataComponent="TableRow"
        :keeps="keeps"
        :scrollRightButton="props.scrollRightButton"
        :extraProps="{
            columns: columnsInner,
            columnsWidth,
            scrollInfo,
            fixedColumns,
            toolsWidth,
            canClickRow: !!attrs.onClickRow,
        }"
        :estimateSize="estimateSize ?? defaultRowHeight"
        :isSizeImmutable="isSizeImmutable"
        :scrollContainerEle="scrollContainerEle"
        :itemClass="`pt-table-row ${itemClass ?? ''} ${!!attrs.onClickRow ? 'pt-table-row--enableClick' : ''} ${
            dataLoading ? 'pt-table-data-loading' : ''
        }`"
        :itemStyle="{ ...itemStyle, height: `${defaultRowHeight}px` }"
        :headerClass="`pt-table-header ${headerClass ?? ''}`"
        :headerStyle="headerStyle"
        :contentStyle="contentStyle"
        :footerClass="footerClass"
        :footerStyle="footerStyle"
        :fixedThumb="fixedThumb"
        :fixedHeader="fixedHeader"
        :fixedFooter="fixedFooter"
        :fixedHeaderWrapperClass="`pt-table-header-fixedWrapper ${fixedHeaderWrapperClass ?? ''} ${
            !scrollContainerEle && fixedHeader ? 'pt-table-header-fixedWrapper2' : ''
        }`"
        :fixedHeaderWrapperStyle="fixedHeaderWrapperStyle"
        :fixedFooterWrapperClass="fixedFooterWrapperClass"
        :fixedFooterWrapperStyle="fixedFooterWrapperStyle"
        :nativeScroll="!(fixedColumns.left?.length > 0 || fixedColumns.right?.length > 0)"
        :autoHide="autoHide"
        @scrollInfo="handleScrollInfoChange"
        :loading="loading"
    >
        <template #header>
            <table-header
                :columns="columns"
                :columnsWidth="columnsWidth"
                :setHeaderWidth="setHeaderWidth"
                :currentSorter="currentSorter"
                :changeCurrentSorter="changeCurrentSorter"
                :fixedColumns="fixedColumns"
                :scrollInfo="scrollInfo"
                :isInPageMode="Boolean(scrollContainerEle)"
                :headerBorder="headerBorder || hasMergeColumn"
            />
        </template>
        <template #footer v-if="(sourceNone || filterNone) && !loading">
            <div ref="footerRef">
                <div v-if="sourceNone && slots.noDataTip" class="pt-table_filterNone">
                    <slot name="noDataTip"></slot>
                </div>
                <div v-else-if="filterNone" class="pt-table_filterNone">
                    {{ typeof $t === 'function' ? $t('pt.table.emptyText') : 'No Data' }}
                </div>
                <!-- <div v-if="loading" class="pt-table_filterNone"></div> -->
            </div>
        </template>
    </virtualized>
</template>

<script lang="ts">
export default {
    name: 'PtTable',
};
</script>

<script lang="ts" setup>
import { ref, reactive, watch, computed, useSlots, useAttrs, provide } from 'vue';
import type { CSSProperties } from 'vue';
import type { ScrollInfo } from '../../scrollbar/src/scrollbar.vue';
import cloneDeep from 'lodash-es/cloneDeep.js';
import Virtualized from '../../virtualized';
import TableHeader from './Header/header.vue';
import TableRow from './Row/row.vue';

type Order = 'desc' | 'asc';

export type Column = {
    key: string;
    name?: string;
    width?: string | number;
    sorter?: true | 'number' | 'string' | ((order: Order) => (a: any, b: any) => number);
    defaultSorter?: boolean;
    fixed?: boolean;
    grow?: boolean | number;
    [other: string]: any;
    children?: Array<Column>;
};

/** 只比virtuailzed多了四个字段, 删了direction和dataComponent */
export interface Props {
    columns: Array<Column>;
    filter?: (source: any) => boolean;
    loading?: boolean;
    dataLoading?: boolean;
    dataKey: string;
    dataSources?: Array<any>;
    pagination?: { startIndex: number; endIndex: number };
    keeps?: number;
    extraProps?: Record<string, any>;
    estimateSize?: number;
    isSizeImmutable?: boolean;
    /** 'documentScroll'(原生页面滚动条) | string(querySelector) | HTMLElement */
    scrollContainerEle?: any; // 这里不能写HTMLElement，devtools会报错
    itemClass?: string;
    itemStyle?: CSSProperties;
    headerClass?: string;
    contentStyle?: CSSProperties;
    headerStyle?: CSSProperties;
    footerClass?: string;
    footerStyle?: CSSProperties;
    fixedThumb?: boolean;
    fixedHeader?: number | boolean;
    fixedFooter?: number | boolean;
    fixedHeaderWrapperClass?: string;
    fixedHeaderWrapperStyle?: CSSProperties;
    fixedFooterWrapperClass?: string;
    fixedFooterWrapperStyle?: CSSProperties;
    autoHide?: boolean;
    toolsWidth?: number | string;
    headerBorder?: boolean;
    defaultRowHeight?: number;
    currentSorter?: string;
    scrollRightButton?: boolean;
    defaultOrder?: Order;
    updateSortedSourcesImmediately?: boolean; //判断是否立即触发update:sortedSources
}

const props = withDefaults(defineProps<Props>(), {
    defaultRowHeight: 88,
    autoHide: true,
    fixedHeader: true,
    fixedThumb: true,
    isSizeImmutable: true,
    toolsWidth: '160px',
    scrollRightButton: false,
    defaultOrder: 'desc',
    updateSortedSourcesImmediately: false,
});
const slots = useSlots();
const attrs = useAttrs();
const emit = defineEmits<{
    (type: 'update:currentSorter', evt: string): void;
    (type: 'update:currentOrder', evt: Order): void;
    (type: 'update:sortedSources', evt: Array<any>): void;
}>();
provide('slots', slots);
defineExpose({
    reorder,
});

export interface SetHeaderWidth {
    (key: string, width: string): void;
}

export interface ChangeCurrentSorter {
    (key: string): void;
}

export interface Comparator {
    (order: Order): (a: any, b: any) => number;
}

export interface CurrentSorter {
    columnKey: string;
    order: Order;
    comparator: (a: any, b: any) => number;
}

const directionMapper = { asc: 1, desc: -1 } as const;

const defaultComparators = {
    number: (order: Order, columnKey: string) =>
        function (a: any, b: any) {
            const comparedA = parseFloat(a[columnKey]);
            const comparedB = parseFloat(b[columnKey]);
            if (comparedA === comparedB) return 0;
            return directionMapper[order] > 0 ? comparedA - comparedB : comparedB - comparedA;
        },

    string: (order: Order, columnKey: string) =>
        function (a: any, b: any) {
            if (a[columnKey] === b[columnKey]) return 0;
            const comparison: boolean =
                directionMapper[order] > 0
                    ? b[columnKey] === null || a[columnKey] > b[columnKey]
                    : a[columnKey] === null || a[columnKey] < b[columnKey];
            return comparison === false ? -1 : Number(comparison) - 0;
        },
};

const findLeftColumnNumber = (column: Column, res: Array<Column>) => {
    if (!column.children) {
        res.push(column);
        return;
    }
    column.children.forEach((column) => findLeftColumnNumber(column, res));
};
const columnsFlat = computed(() => {
    const res: Array<Column> = [];
    props.columns.forEach((column) => findLeftColumnNumber(column, res));
    return res;
});

const leafColumnNumer = computed(() => columnsFlat.value.length);
const columnsWidth = ref<Record<string, string>>({});
const columnsWidthTemp: any = {};

const columnsInner = ref<Props['columns']>(columnsFlat.value ?? []);
const hasMergeColumn = computed(() => !!props.columns?.find((column) => column.children));
const currentSorter = ref<CurrentSorter | undefined>();

const changeCurrentSorter = (key: string, newColumns?: Props['columns']) => {
    const targetColumns = newColumns || columnsInner.value;
    const targetColumn = targetColumns?.find((column) => column.key === key);
    if (!targetColumn || !targetColumn.sorter) return;

    /** 点新列或者初始化sorter时候降序，点相同列切换 */
    const newOrder = !currentSorter.value
        ? props.defaultOrder
        : currentSorter.value.columnKey !== targetColumn.key || currentSorter.value?.order === 'asc'
        ? 'desc'
        : 'asc';
    emit('update:currentOrder', newOrder);
    const comparator =
        typeof targetColumn.sorter === 'function'
            ? targetColumn.sorter(newOrder)
            : targetColumn.sorter === true || targetColumn.sorter === 'string'
            ? defaultComparators.string(newOrder, targetColumn.key)
            : targetColumn.sorter === 'number'
            ? defaultComparators.number(newOrder, targetColumn.key)
            : undefined;

    if (!comparator) return;

    if (targetColumn.key !== currentSorter.value?.columnKey) {
        emit('update:currentSorter', targetColumn.key);
    }
    if (!currentSorter.value)
        currentSorter.value = {
            columnKey: targetColumn.key,
            comparator,
            order: newOrder,
        };
    else Object.assign(currentSorter.value, { columnKey: targetColumn.key, order: newOrder, comparator });
};

/**
 * 按现有排序字段重新执行一遍排序
 * 外部调用
 */
function reorder() {
    if (currentSorter.value?.order) {
        const targetColumns = columnsInner.value;
        const targetColumn = targetColumns?.find((column) => column.key === currentSorter.value?.columnKey);
        if (!targetColumn || !targetColumn.sorter) return;

        const comparator =
            typeof targetColumn.sorter === 'function'
                ? targetColumn.sorter(currentSorter.value?.order)
                : targetColumn.sorter === true || targetColumn.sorter === 'string'
                ? defaultComparators.string(currentSorter.value?.order, targetColumn.key)
                : targetColumn.sorter === 'number'
                ? defaultComparators.number(currentSorter.value?.order, targetColumn.key)
                : undefined;
        comparator && (currentSorter.value.comparator = comparator);
    }
}

watch(
    () => props.currentSorter,
    (preVal, nextVal) => {
        if (!props.currentSorter || props.currentSorter === currentSorter.value?.columnKey) return;
        changeCurrentSorter(props.currentSorter);
    },
);

watch(
    () => [columnsFlat.value, slots],
    () => {
        if (!props.columns) return;
        const newColumnsInner = cloneDeep(columnsFlat.value);

        let firstSorterKey: string | undefined = undefined; // 寻找第一个有sorter的column
        let hasCurrentSorterInColumns = false; // 判断当前sorter是否在变动后的columns中
        newColumnsInner?.forEach((column, index) => {
            if (typeof column?.width === 'number') column.width = `${column.width}px`; // 如果column的width为number，转化为px

            if (firstSorterKey === undefined && column.sorter) firstSorterKey = column.key;
            if (!currentSorter.value && column.defaultSorter) {
                changeCurrentSorter(column.key, newColumnsInner);
                firstSorterKey = undefined;
            }
            if (currentSorter.value && column.key === currentSorter.value.columnKey) hasCurrentSorterInColumns = true;
        });
        /** 如果初始化sorter时候未找到defaultSorter，或者当前的currentSorter不在变动后的columns中，设置columns中的第一个sorter为当前sorter */
        if (firstSorterKey !== undefined && (!hasCurrentSorterInColumns || !currentSorter.value))
            changeCurrentSorter(firstSorterKey, newColumnsInner);

        columnsInner.value = newColumnsInner;

        Object.keys(columnsWidthTemp).forEach((columnKey) => {
            if (!columnsFlat.value?.find((column) => column.key === columnKey)) delete columnsWidthTemp[columnKey];
        });
    },
    { immediate: true },
);

const usedDataSource = computed(() => {
    let resDataSource = props.dataSources ?? [];
    if (currentSorter.value || props.filter) resDataSource = resDataSource.slice();
    if (props.filter) resDataSource = resDataSource.filter(props.filter);
    const headers = resDataSource.filter((data) => data._header);
    const footers = resDataSource.filter((data) => data._footer);
    let sortable = resDataSource.filter((data) => !data._footer && !data._header);
    if (currentSorter.value) sortable = sortable.sort(currentSorter.value.comparator);
    return [
        ...headers,
        ...(props.pagination
            ? sortable.slice(
                  Math.max(0, props.pagination.startIndex),
                  Math.min(props.dataSources?.length ?? Infinity, props.pagination.endIndex),
              )
            : sortable),
        ...footers,
    ];
});
watch(usedDataSource, () => emit('update:sortedSources', usedDataSource.value), {
    immediate: props.updateSortedSourcesImmediately,
});

/** 未在column中声明width值的列，应用header的width，设置值在innerColumn的_headerWidth中 */
const setHeaderWidth = (key: string, width: string) => {
    columnsWidthTemp[key] = width;
    if (Object.keys(columnsWidthTemp).length !== leafColumnNumer.value) return;
    columnsWidth.value = { ...columnsWidthTemp };
};

const filterNone = computed(() => usedDataSource.value?.length == 0);
const sourceNone = computed(() => !props.dataSources || props.dataSources?.length === 0);

const scrollInfo = reactive({
    scrollLeft: 0,
    maxScrollLeft: 0,
    scrollTop: 0,
    maxScrollTop: 0,
});
const handleScrollInfoChange = (newScrollInfo: ScrollInfo) => Object.assign(scrollInfo, newScrollInfo);

const fixedColumns = computed(() => {
    const res: { left: Props['columns']; right: Props['columns']; hasTools: boolean } = {
        left: [],
        right: [],
        hasTools: false,
    };
    const middleIndex = (columnsInner.value?.length / 2) | 0;
    let couldFixed = true;
    for (let start = 0, end = middleIndex; start < end; start++) {
        if (columnsInner.value[start].fixed && couldFixed) res.left.push(columnsInner.value[start]);
        if (!columnsInner.value[start].fixed) couldFixed = false;
    }

    if (!slots.tools) {
        couldFixed = true;
        for (let start = columnsInner.value?.length - 1, end = middleIndex; start >= end; start--) {
            if (columnsInner.value[start].fixed && couldFixed) res.right.push(columnsInner.value[start]);
            if (!columnsInner.value[start].fixed) couldFixed = false;
        }
    } else {
        res.right.push({ key: 'tools' });
        res.hasTools = true;
    }
    return res;
});

const tableRef = ref();
const footerRef = ref();
const resizeFooter = () => {
    if (!footerRef.value) return;
    const totalWidth = Object.values(columnsWidth.value).reduce((pre, cur) => pre + parseInt(cur), 0);
    requestAnimationFrame(() => {
        if (!footerRef.value) return;
        footerRef.value.style.width = `${totalWidth}px`;
    });
};
watch(() => columnsWidth.value, resizeFooter);
</script>

<style lang="scss">
.pt-table {
    border: 1px solid #dfe1e6;
    contain: style;

    .pt-table-row.pt-table-data-loading {
        .pt-table-row_cell {
            background-color: transparent !important;
        }
    }
}

.pt-table_filterNone {
    position: relative;
    height: 320px;
    // line-height: 200px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    background-color: #ffffff;
    & .el-loading-spinner {
        top: 14px !important;
        margin-top: 0 !important;
    }
    // & .pt-loading-spinner {
    //     top: 14px !important;
    //     margin-top: 0 !important;
    // }
}
</style>
